import { Button, FormControl, Note, Stack, Textarea } from '@contentful/f36-components';
import React, { useState } from 'react';
import { MsxProduct } from '../clients/MsxService';
import { parseCsvToMpvIds } from '../helper/csvProductParser';
import { TestIds } from '../fixtures/constants';

interface ProductsCsvInputProps {
  allProducts: MsxProduct[];
  defaultLocale: string;
  isLoadingProducts: boolean;
  selectedProducts: MsxProduct[];
  onBulkSetProducts: (products: MsxProduct[]) => void;
}

const resolveToMsxProducts = (mpvIds: string[], allProducts: MsxProduct[]): MsxProduct[] => {
  return mpvIds.map((mpvId) => {
    const match = allProducts.find((p) => p.mpvId.toLowerCase() === mpvId.toLowerCase());
    return match ?? { mpvId, productKey: '' };
  });
};

const getUnknownMpvIds = (selectedProducts: MsxProduct[], allProducts: MsxProduct[]): string[] => {
  return selectedProducts
    .filter((p) => !allProducts.some((ap) => ap.mpvId.toLowerCase() === p.mpvId.toLowerCase()))
    .map((p) => p.mpvId);
};

export const ProductsCsvInput = (props: ProductsCsvInputProps): React.JSX.Element => {
  const [csvInput, setCsvInput] = useState<string>(props.selectedProducts.map((p) => p.mpvId).join(', '));

  const handleApply = () => {
    const mpvIds = parseCsvToMpvIds(csvInput);
    const resolved = resolveToMsxProducts(mpvIds, props.allProducts);
    props.onBulkSetProducts(resolved);
  };

  const unknownMpvIds =
    !props.isLoadingProducts && props.allProducts.length > 0
      ? getUnknownMpvIds(props.selectedProducts, props.allProducts)
      : [];

  return (
    <Stack flexDirection="column" alignItems="flex-start" style={{ width: '100%' }}>
      <Stack flexDirection="column" alignItems="flex-start" spacing="spacingXs">
        <FormControl>
          <FormControl.Label>Paste mpvIds (comma or newline separated)</FormControl.Label>
          <div data-test-id={TestIds.PRODUCTS_CSV_INPUT}>
            <Textarea
              value={csvInput}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCsvInput(e.target.value)}
              placeholder="e.g. basicTshirt, standardMug, classicCap"
              rows={4}
            />
          </div>
        </FormControl>
        <div data-test-id={TestIds.PRODUCTS_CSV_APPLY_BUTTON}>
          <Button variant="primary" onClick={handleApply} isDisabled={props.isLoadingProducts}>
            {props.isLoadingProducts ? 'Loading product catalog...' : 'Apply'}
          </Button>
        </div>
      </Stack>
      {!props.isLoadingProducts && props.allProducts.length === 0 && (
        <Note variant="neutral">
          No products found for this locale. IDs will be accepted but cannot be validated against the catalog.
        </Note>
      )}
      {unknownMpvIds.length > 0 && (
        <Note data-test-id={TestIds.PRODUCTS_CSV_UNKNOWN_WARNING} variant="warning">
          The following mpvIds were not found in the default locale "{props.defaultLocale}":{' '}
          <strong>{unknownMpvIds.join(', ')}</strong>. They may be malformed, or they may be valid products in other
          locales that are not loaded for the current locale.
        </Note>
      )}
    </Stack>
  );
};
